import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQueryWithReauth } from 'src/apis/baseQuery';
import type { RootState } from 'src/store';
import {
  DataDTOGenerateEpcRequest,
  DataDTOGenerateEpcResponse,
  DataDTOPagedDataDataDTOProductListing,
  DataDTOProductCrud,
  DataDTOProductDebug,
  DataDTOProductListing,
  DataDTOProductView,
} from 'src/types/api/generated/api';
import { PaginationParams } from 'src/types/common';
import { DecodeProductIdentifierParams } from 'src/types/productIdentifier';

export const productAPI = createApi({
  reducerPath: 'productAPI',
  baseQuery: axiosBaseQueryWithReauth({
    baseUrl: process.env.REACT_APP_API_URL,
  }),
  tagTypes: ['Product'],
  endpoints: (builder) => ({
    getProducts: builder.query<
      DataDTOPagedDataDataDTOProductListing,
      PaginationParams & { clientId: number }
    >({
      query: ({ page, pageSize, orderBy, filters, searchTerm, clientId }) => ({
        url: 'Products',
        method: 'GET',
        params: {
          page,
          pageSize,
          orderBy,
          searchTerm,
          filters: filters ? JSON.stringify(filters) : undefined,
          clientId,
        },
      }),
      providesTags: [{ type: 'Product', id: 'LIST' }],
    }),

    getProductById: builder.query<DataDTOProductView, { id: number; clientId: number }>({
      query: ({ id, clientId }) => ({
        url: `Products/${id}`,
        method: 'GET',
        params: { clientId },
      }),
      providesTags: (_result, _error, { id }) => [{ type: 'Product', id }],
    }),

    patchProductIsDeleted: builder.mutation<unknown, { id: number; isDeleted: boolean }>({
      query: ({ id, isDeleted }) => ({
        url: `Products/${id}/isDeleted/${isDeleted}`,
        method: 'PATCH',
      }),
      async onQueryStarted({ id, isDeleted }, { dispatch, queryFulfilled, getState }) {
        const cachedArgs = productAPI.util.selectCachedArgsForQuery(
          getState() as RootState,
          'getProducts',
        );

        const patches = cachedArgs.map((args) =>
          dispatch(
            productAPI.util.updateQueryData('getProducts', args, (draft) => {
              const product = draft.rows.find((row: DataDTOProductListing) => row.id === id);
              if (product) {
                product.isDeleted = isDeleted;
              }
            }),
          ),
        );

        try {
          await queryFulfilled;
        } catch {
          patches.forEach((p) => p.undo());
          dispatch(productAPI.util.invalidateTags([{ type: 'Product', id: 'LIST' }]));
        }
      },
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Product', id: 'LIST' },
        { type: 'Product', id },
      ],
    }),

    deleteAllProducts: builder.mutation<boolean, { clientId: number }>({
      query: ({ clientId }) => ({
        url: `Clients/${clientId}/Products`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Product', id: 'LIST' }],
    }),

    createProduct: builder.mutation<DataDTOProductView, DataDTOProductCrud>({
      query: (body) => ({
        url: 'Products',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Product', id: 'LIST' }],
    }),

    updateProduct: builder.mutation<
      DataDTOProductView,
      { id: number; body: DataDTOProductCrud }
    >({
      query: ({ id, body }) => ({
        url: `Products/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: [{ type: 'Product', id: 'LIST' }],
    }),

    generateEpc: builder.mutation<DataDTOGenerateEpcResponse, DataDTOGenerateEpcRequest>({
      query: (payload) => ({
        url: 'Commissions/generate-epc',
        method: 'POST',
        body: payload,
      }),
    }),

    decodeProductIdentifier: builder.query<DataDTOProductDebug, DecodeProductIdentifierParams>({
      query: (params) => ({
        url: 'Products/decodeProductIdentifier',
        method: 'GET',
        params,
      }),
    }),

    exportProducts: builder.query<
      Blob,
      {
        clientId: number;
        startDate?: string;
        endDate?: string;
        searchTerm?: string;
        filters?: Record<string, unknown>;
      }
    >({
      query: ({ clientId, startDate, endDate, searchTerm, filters }) => ({
        url: 'Products/export',
        method: 'GET',
        params: {
          clientId,
          startDate,
          endDate,
          searchTerm,
          filters: filters ? JSON.stringify(filters) : undefined,
        },
        responseType: 'blob',
      }),
    }),
  }),
});

export const {
  useGetProductsQuery,
  useLazyGetProductsQuery,
  useGetProductByIdQuery,
  usePatchProductIsDeletedMutation,
  useDeleteAllProductsMutation,
  useCreateProductMutation,
  useUpdateProductMutation,
  useGenerateEpcMutation,
  useLazyDecodeProductIdentifierQuery,
  useLazyExportProductsQuery,
} = productAPI;
